import { ActionButton, makeStyles, Typography, useTranslation } from '@paintscout/ui'
import type { Action, MuiTheme as Theme } from '@paintscout/ui'
import type { RenderableItem } from '@paintscout/util/builder'
import React from 'react'
import { useTranslatedNoteContent } from '../Quote/views/WorkOrderView/useTranslatedNoteContent'
import { translateStructuredWorkOrderContent } from '../Quote/views/WorkOrderView/WorkOrderCard/translateStructuredWorkOrderContent'
import WorkOrderItemRows, { getItemHourTotals, resolveName } from './WorkOrderItemRows'
import { formatHourTotal } from './workOrderHours'
import WorkOrderTotalFooter from './WorkOrderTotalFooter'

export interface WorkOrderOptionRowContentProps {
  item: RenderableItem
  /** Show hour values (the Work Order's per-item "show hours" setting). */
  showHours?: boolean
  /** The account's labels for the footer's hour split, already translated by the table. */
  prepLabel: string
  labourLabel: string
  /** Kebab actions; when omitted (or empty) the kebab is hidden. */
  actions?: Action[]
  onAction?: (action: string, item: RenderableItem) => void
}

const useStyles = makeStyles(
  (theme: Theme) => {
    const neutral = theme.paintscout.palette.brand.Neutral
    return {
      // The kebab is taken out of the flow (see actionColumn) so the rows run the full width of the
      // row and their hour values sit hard against its right edge, in line with the Total bar below.
      root: {
        position: 'relative'
      },
      // The option's own name, in place of the hour card's grey header band. Its hours live in the
      // Total bar below, exactly as they do on a card. The right padding keeps a long name clear of
      // the kebab floating above it.
      name: {
        display: 'block',
        fontWeight: 500,
        fontSize: theme.typography.pxToRem(15),
        color: neutral.P35,
        minWidth: 0,
        paddingRight: theme.spacing(4),
        marginBottom: theme.spacing(1)
      },
      // The bar lines up with the row's text: the side margins cancel only the tray's own 4px, so the
      // bar's edges sit flush with the content column. The bottom one pulls it down to the row's edge
      // so it sits right above the divider below.
      footerTray: {
        marginTop: theme.spacing(1),
        marginLeft: theme.spacing(-0.5),
        marginRight: theme.spacing(-0.5),
        marginBottom: theme.spacing(-1)
      },
      actionColumn: {
        position: 'absolute',
        top: -8,
        right: 0,
        '& svg': {
          color: neutral.P60
        },
        '@media print': {
          display: 'none'
        }
      }
    }
  },
  { name: 'WorkOrderOptionRowContent' }
)

/**
 * An Options row in the Work Order's Crew/Hours view: the hour card's body (`WorkOrderItemRows`)
 * embedded in the Options table, so an option reads like the Area/Substrate cards above it — the
 * option's name and hours on top, then its crew description, products, notes and Coats beneath
 * (ENG-1850). The card's grey header band and its rules between sub-items are deliberately dropped;
 * the table row is the container here.
 */
export default function WorkOrderOptionRowContent({
  item,
  showHours,
  prepLabel,
  labourLabel,
  actions,
  onAction
}: WorkOrderOptionRowContentProps) {
  const classes = useStyles()
  const { __, lang } = useTranslation()

  const name = resolveName(item.name)
  const [translatedInputs] = useTranslatedNoteContent([name])
  const translatedName = translateStructuredWorkOrderContent(translatedInputs[0] || name, __)

  const { prep, painting, total } = getItemHourTotals(item)
  // The split only earns its place when it reconciles with the total: an option whose hours are
  // overridden (`item.value` wins), or one with no prep/painting split at all, would otherwise show
  // numbers that don't add up — which reads as a bug to a crew. Same rule as WorkOrderCardNext.
  const splitAddsUp = Math.abs(prep + painting - total) < 0.005
  const footerParts = [
    ...(splitAddsUp
      ? [`${prepLabel}: ${formatHourTotal(prep, lang)}`, `${labourLabel}: ${formatHourTotal(painting, lang)}`]
      : []),
    `${__('Total')}: ${formatHourTotal(total, lang)}`
  ]

  return (
    <div className={classes.root}>
      <Typography className={classes.name}>{translatedName}</Typography>
      <WorkOrderItemRows item={item} showHours={showHours} variant="embedded" />
      {actions && actions.length > 0 && onAction && (
        <div className={`${classes.actionColumn} no-drag`}>
          <ActionButton nextMenu actions={actions} onActionClick={(_ev, action) => onAction(action, item)} />
        </div>
      )}
      {showHours && <WorkOrderTotalFooter className={classes.footerTray} label={__('Total')} parts={footerParts} />}
    </div>
  )
}
